import { useSelector } from "react-redux";

import { Download } from "@mui/icons-material";
import { IconButton } from "@mui/material";

import { Tooltip } from "components/Tooltip";

import { i18n } from "services/i18nService";

export function DownloadTableToExcel(props) {
  const {
    tableRef,
    fileName,
    columnConfig,
    iconSx = {},
    iconStyle = {},
    records,
  } = props;

  const defaultReportFormat =
    useSelector(
      (state) => state.userCompanySettings.settings.defaultReportFormat
    ) ?? "xlsx";

  const isLoading = useSelector(({ loading }) => loading.list);

  async function downloadListToExcel() {
    // exit early if the table DOM ref isn't available
    if (!tableRef.current) {
      return;
    }
    // dynamically import the required xlsx utilities
    const { utils, writeFile } = await import("xlsx");

    // convert the HTML table into a worksheet object
    const rawSheet = utils.table_to_sheet(tableRef.current, { raw: true });

    // convert worksheet to a 2D array: first row is headers, rest are data
    const [headers, ...rows] = utils.sheet_to_json(rawSheet, {
      header: 1, // use the first row as header
      defval: "", // fill empty cells with an empty string
    });

    // identify indexes of non-empty header columns
    const validIndexes = headers
      .map((h, i) => (h ? i : null))
      .filter((i) => i !== null);

    // extract non-empty headers for use in the final Excel sheet
    const nonEmptyHeaders = validIndexes.map((i) => headers[i]);

    // map each row to a JSON object using only the valid (non-empty) headers
    const jsonData = rows.map((row) =>
      validIndexes.reduce((obj, colIndex) => {
        obj[headers[colIndex]] = row[colIndex];
        return obj;
      }, {})
    );

    // apply any custom downloadFn transformation functions per column config
    const transformedData = columnConfig
      ? jsonData.map((row, i) => {
          return Object.entries(row).reduce((seed, [key, value]) => {
            const config = columnConfig.find(({ heading }) => heading === key);

            if (config) {
              const { downloadFn, name, fieldName } = columnConfig.find(
                ({ heading }) => heading === key
              );

              const valueKey = fieldName || name;
              const record = records[i];

              // use the custom download function if it exists; otherwise, use the raw value
              seed[key] = downloadFn
                ? downloadFn(record[valueKey], record)
                : value;
            } else {
              // custom field columns don't have config values in columnConfig
              // so just return the value present on html UI
              seed[key] = value;
            }

            return seed;
          }, {});
        })
      : jsonData;

    // convert the transformed data into a worksheet with correct headers
    const cleanedSheet = utils.json_to_sheet(transformedData, {
      header: nonEmptyHeaders,
    });

    if (defaultReportFormat === "csv") {
      // export as CSV
      const csvString = utils.sheet_to_csv(cleanedSheet);
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    } else {
      // create a new workbook and append the sheet
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, cleanedSheet, "Sheet1");

      // trigger download of the workbook as an Excel file
      writeFile(workbook, `${fileName}.${defaultReportFormat}`);
    }
  }

  return (
    <Tooltip title={i18n("tooltip.DownloadDisplayedList")}>
      <div>
        <IconButton
          sx={iconSx}
          style={iconStyle}
          color="primary"
          onClick={() => downloadListToExcel()}
          disabled={isLoading}
        >
          <Download />
        </IconButton>
      </div>
    </Tooltip>
  );
}
